import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faSpinner } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import { primaryColor, secondaryColor } from "../../../../utils/comon";

const getNowDatetime = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const h = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d}T${h}:${min}`;
};

const VisitorForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = id && id !== "add";

    const employeeCode = storageService.getItem("employeeCode");
    const currentLoginName = storageService.getItem("employeeName");

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        visitorName: "",
        visitorMobileNumber: "",
        visitorEmailId: "",
        visitorLocation: "",
        PurposeOfVisit: "",
        expectedEntryTime: getNowDatetime(),
        expectedExitTime: "",
        userId: employeeCode,
        hostname: currentLoginName,
        siteId: 0,
        unitId: 0,
        flatName: ""
    });

    useEffect(() => {
        const fetchVisitorDetails = async () => {
            if (!isEditMode) return;
            setLoading(true);
            try {
                const response = await apiServices({
                    method: "GET",
                    url: `/ResidentialInviteVisitor/CheckInInviteVisitor/${employeeCode}`
                });
                if (response?.success && response?.data?.data) {
                    const visitor = response.data.data.find(v => v.visitorId === parseInt(id));
                    if (visitor) {
                        setFormData({
                            ...visitor,
                            expectedEntryTime: visitor.expectedEntryTime
                                ? new Date(visitor.expectedEntryTime).toISOString().slice(0, 16) : "",
                            expectedExitTime: visitor.expectedExitTime
                                ? new Date(visitor.expectedExitTime).toISOString().slice(0, 16) : ""
                        });
                    } else {
                        toast.error("Visitor not found.");
                        navigate("/zentres/visitor-management/visitor");
                    }
                }
            } catch (error) {
                toast.error("Error fetching visitor details");
            } finally {
                setLoading(false);
            }
        };

        fetchVisitorDetails();
    }, [id, isEditMode, employeeCode, navigate]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));

        // Keep minExitDate in sync with entry time (mirrors Angular valueChanges pattern)
        if (id === "expectedEntryTime") {
            setFormData(prev => ({ ...prev, [id]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.visitorName.trim() || !formData.visitorMobileNumber.trim() || !formData.visitorLocation.trim()) {
            toast.warning("Please fill in all required fields.");
            return;
        }

        if (formData.visitorMobileNumber.length !== 10) {
            toast.warning("Mobile number must be exactly 10 digits.");
            return;
        }

        if (!/^[0-9]{10}$/.test(formData.visitorMobileNumber)) {
            toast.warning("Mobile number must contain only digits.");
            return;
        }

        if (formData.visitorEmailId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.visitorEmailId)) {
            toast.warning("Please enter a valid email address.");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                userId: employeeCode,
                hostname: currentLoginName
            };

            const response = await apiServices({
                method: "POST",
                url: `/ResidentialInviteVisitor/SaveInviteVisitor`,
                data: payload
            });

            if (response?.success) {
                toast.success(isEditMode ? "Visitor updated successfully" : "Visitor invited successfully");
                navigate("/zentres/visitor-management/visitor");
            } else {
                toast.error(response?.message || "Failed to save visitor");
            }
        } catch (error) {
            console.error("Error saving visitor:", error);
            toast.error("Error saving visitor details");
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass = "w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400";

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full w-full bg-white rounded-lg">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500 text-3xl" />
            </div>
        );
    }

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />

            <PageHeadingSection
                Navbar={true}
                mainHeading={isEditMode ? "Edit Visitor" : "Invite Visitor"}
                subHeading={`Please fill out the form below to ${isEditMode ? 'update the visitor invitation' : 'invite a new visitor'}.`}
            />

            <div className="mt-6 w-full max-w-5xl">
                <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 flex flex-col gap-6 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Visitor Name */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="visitorName">
                                Visitor Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="visitorName"
                                value={formData.visitorName}
                                onChange={handleInputChange}
                                placeholder="Enter full name"
                                className={inputClass}
                                required
                            />
                        </div>

                        {/* Mobile Number */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="visitorMobileNumber">
                                Mobile Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="tel"
                                id="visitorMobileNumber"
                                value={formData.visitorMobileNumber}
                                onChange={handleInputChange}
                                placeholder="10 digit mobile number"
                                maxLength="10"
                                className={inputClass}
                                required
                            />
                        </div>

                        {/* Email */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="visitorEmailId">
                                Email
                            </label>
                            <input
                                type="email"
                                id="visitorEmailId"
                                value={formData.visitorEmailId}
                                onChange={handleInputChange}
                                placeholder="Enter email address"
                                className={inputClass}
                            />
                        </div>

                        {/* Location */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="visitorLocation">
                                Location <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="visitorLocation"
                                value={formData.visitorLocation}
                                onChange={handleInputChange}
                                placeholder="Enter location / coming from"
                                className={inputClass}
                                required
                            />
                        </div>

                        {/* Purpose of Visit */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="PurposeOfVisit">
                                Purpose of Visit
                            </label>
                            <input
                                type="text"
                                id="PurposeOfVisit"
                                value={formData.PurposeOfVisit}
                                onChange={handleInputChange}
                                placeholder="e.g. Personal, Delivery, Work"
                                className={inputClass}
                            />
                        </div>

                        {/* Expected Entry Time */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="expectedEntryTime">
                                Expected Entry Time
                            </label>
                            <input
                                type="datetime-local"
                                id="expectedEntryTime"
                                value={formData.expectedEntryTime}
                                onChange={handleInputChange}
                                min={getNowDatetime()}
                                className={inputClass}
                            />
                        </div>

                        {/* Expected Exit Time */}
                        <div className="flex flex-col gap-2">
                            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider" htmlFor="expectedExitTime">
                                Expected Exit Time
                            </label>
                            <input
                                type="datetime-local"
                                id="expectedExitTime"
                                value={formData.expectedExitTime}
                                onChange={handleInputChange}
                                min={formData.expectedEntryTime || getNowDatetime()}
                                className={inputClass}
                            />
                        </div>

                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex justify-between items-center mt-6 pt-6 mb-8">
                    <button
                        type="button"
                        onClick={() => navigate("/zentres/visitor-management/visitor")}
                        className="px-6 py-2.5 border border-gray-200 rounded-xl text-xs font-bold flex items-center gap-2 text-gray-600 hover:bg-gray-50 active:scale-95 transition-all shadow-sm"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                        Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-8 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50"
                        style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                        onMouseEnter={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = primaryColor();
                                e.currentTarget.style.color = '#FFFFFF';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!submitting) {
                                e.currentTarget.style.backgroundColor = secondaryColor();
                                e.currentTarget.style.color = primaryColor();
                            }
                        }}
                    >
                        {submitting
                            ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                            : (isEditMode ? 'Update Visitor' : 'Invite Visitor')
                        }
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VisitorForm;
